import { getDefaultStore } from 'jotai';

import { MedicalDirectorSidebar } from '@/integrations/medical-director/medicalDirectorSidebar';
import { store as reduxStore } from '@/store';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { logger } from '@/utils/logger/logger';
import {
  getExternalLaunchProvider,
  setExternalLaunchToken,
} from '@heidi/integrations/ehr/data/atoms';

import {
  authCacheSuppressedAtom,
  authSessionMetadataAtom,
  authStateAtom,
  authTransientFailureAtom,
  authUnauthenticatedReasonAtom,
  readAuthRevision,
} from '@heidi/auth/state';
import { getCurrentAuthIdentitySnapshot } from './authIdentitySnapshot';
import { IS_DUAL_RUN_ACTIVE } from './constants';
import { getEffectiveAuthProvider } from './effectiveAuthProvider';
import { readFromProviderSourceOfTruth } from './readFromProviderSourceOfTruth';
import type { AuthSessionMetadata } from './types';

/** Background refresh fires when the cached token is within this many ms of expiry. */
export const REFRESH_SKEW_MS = 30_000;

/** Hard timeout on `readFromProviderSourceOfTruth` for every branch except Auth0-desktop. */
export const AUTH_FETCH_TIMEOUT_MS = 10_000;

/**
 * Hard timeout on `readFromProviderSourceOfTruth` when the effective
 * provider is `AUTH0_DESKTOP`.
 *
 * The Rust plugin's `auth_get_access_token` has a 10s connect timeout
 * (`tauri-plugin-auth0/src/oauth.rs:21`) and `offlineDesktopFallback`'s
 * `getAuthSession()` joins that same in-flight refresh via the Rust
 * single-flight — so on a black-holed network the join resolves at
 * roughly the Rust connect deadline, ~t≈10s. With the shared 10s FE
 * global, the fallback races the outer timeout at the exact same
 * instant and loses; ~13s gives the fallback headroom to resolve and
 * apply the persisted-token success outcome before the outer timeout
 * fires. Only Auth0-desktop needs the extra budget — no other branch
 * shares the Rust refresh path.
 *
 * The Rust TOTAL request timeout is 30s
 * (`tauri-plugin-auth0/src/oauth.rs:18`); a stalled-after-connect
 * network still parks the first attempt(s) and only recovers when a
 * later boot-retry's fallback read catches the 30s completion. The
 * durable fix — a no-refresh session read from the Rust plugin —
 * belongs to a later stack step that also migrates the persisted-
 * token source off `readPersistedAuthSlice`. Do NOT add the Rust
 * command here.
 */
export const AUTH0_DESKTOP_FETCH_TIMEOUT_MS = 13_000;

/** Fallback expiry when a JWT's `exp` claim can't be decoded — 5 minutes. */
const FALLBACK_TTL_MS = 5 * 60_000;

interface CachedSlot {
  token: string;
  expiresAt: number;
  session: AuthSessionMetadata;
}

let cached: CachedSlot | null = null;
let inFlight: Promise<LaunchFetchResult> | null = null;
let inFlightIsForcedRefresh = false;

/**
 * Incremented by `clearAuthCache()`. An in-flight fetch captures the value
 * at launch; if the counter has advanced by the time it returns, the fetch
 * is invalidated and its result is discarded. Guards against a stale
 * response overwriting the cache after a logout.
 */
let generation = 0;

export interface ClearAuthCacheOptions {
  /**
   * Why the cache is being cleared. When `'logout'`, the helper
   * transitions `authStateAtom` to `'unauthenticated'` and sets the reason
   * atom to `'explicit_logout'` — the telemetry subscriber gates on this
   * to suppress a `debouncedExpiryLogout` call on intentional logout.
   *
   * Omit for transitions where the next state is unknown at clear time
   * (e.g. post-login flush, where the caller follows up with
   * `await getAccessToken()` to drive `authStateAtom` to
   * `'authenticated'`).
   */
  reason?: 'logout';
}

export function clearAuthCache(options: ClearAuthCacheOptions = {}): void {
  const store = getDefaultStore();

  cached = null;
  generation += 1;
  inFlight = null;
  inFlightIsForcedRefresh = false;

  store.set(authSessionMetadataAtom, null);
  store.set(authTransientFailureAtom, false);

  if (options.reason === 'logout') {
    store.set(authStateAtom, 'unauthenticated');
    store.set(authUnauthenticatedReasonAtom, 'explicit_logout');
  }
  // No reason supplied — leave authStateAtom + reason untouched; the caller
  // is responsible for the next transition (typically via an immediate
  // `await getAccessToken()`).
}

export interface GetAccessTokenOptions {
  /**
   * Force a refresh even if the cached token is still valid. Used by the
   * axios 401-retry handler when the BE has rejected a token the cache
   * thinks is fresh — the cache must round-trip the provider to either
   * recover or confirm the session is dead.
   */
  forceRefresh?: boolean;
  /**
   * The bearer token that produced the 401 triggering this forced refresh.
   * When supplied, the serialize path can tell a genuinely fresh in-flight
   * result (different token) from the same stale one and skip the second
   * round-trip in the former case. Provided by the 401-retry handler.
   * Omitting it is safe: the in-flight result is returned regardless if
   * it is non-null, which is correct when the prior token is unknown.
   */
  priorToken?: string;
  /**
   * Provider + authRevision the caller's request context was captured
   * under. Only meaningful with `forceRefresh: true`. This caller's OWN
   * classification, independent of every other caller (leader or joiner)
   * that may be sharing the same underlying fetch: rechecked against live
   * state fresh after each await this caller takes, right up to the point
   * the shared work settles — a mismatch at any of those points discards
   * it as `'stale'` for THIS caller, the same way a generation change
   * already does, regardless of whether the shared fetch itself succeeded
   * for some other, still-current caller. Never compared again after a
   * refresh this caller accepts commits: the post-commit revision report
   * is unconditional.
   */
  expected?: { provider: string; authRevision: number };
}

/** See {@link getAccessTokenWithRevision}. */
export type AccessTokenOutcome =
  | { status: 'success'; token: string; authRevision: number }
  | { status: 'stale' }
  | { status: 'no-token' };

interface LaunchFetchResult {
  token: string | null;
  /**
   * True only when the shared operation itself discarded its result
   * because live identity moved between when it launched and when it
   * completed — structural, and shared by every caller waiting on this
   * fetch: nobody accepts a token that no longer represents anyone's live
   * session, so this applies uniformly regardless of any individual
   * caller's own `expected`. Distinct from a generation/suppression
   * discard, so callers can skip atom-based classification and propagate
   * non-destructively.
   *
   * `false` here is NOT itself a success signal for any particular
   * caller — it only means the shared identity stayed stable across the
   * operation. Each caller still separately classifies the (possibly
   * successful) result against its OWN `expected` via `launchIdentity`
   * below (see `isStaleForCaller` and the call sites in
   * `getAccessTokenWithRevision`): a caller whose own captured context
   * doesn't match the identity this fetch launched under gets `'stale'`
   * there even when `stale` is `false` here — this is what lets a leader
   * whose own request predates a session that was ALREADY live before
   * this fetch even started correctly get `'stale'`, while a
   * differently-captured, still-current joiner on the SAME fetch accepts
   * the very same result as success.
   */
  stale: boolean;
  /**
   * Provider + authRevision live when this operation launched — fixed for
   * the whole invocation, regardless of which caller ends up triggering it
   * or how many others join. Compare a caller's own `expected` against
   * THIS, never against live state read after the fact: live state has,
   * by definition, already moved past `expected` for a caller whose own
   * accepted refresh is the very thing that just advanced it (see
   * `isStaleForCaller`).
   */
  launchIdentity: { provider: string; authRevision: number };
}

/**
 * Sync accessor for the currently-cached access token. Does NOT launch a
 * fetch and does NOT consult expiry — returns the last-known-good token the
 * cache saw, or `null` if the slot is empty.
 *
 * Purpose: sync direct-consumer helpers (`getAuthTokenForHttpClientSync` in
 * `src/utils/http/`) whose caller signatures cannot accept a Promise —
 * `Record<string, string>` header builders, Tiptap command handlers that
 * must return synchronously, etc.
 *
 * Consumers MUST NOT interpret a `null` return as "user logged out." A
 * warm-up-in-progress cache returns `null` even for an authenticated user;
 * the axios retry handler + atom subscribers own the logout decision.
 *
 * Expiry is deliberately NOT checked: sync consumers can't refresh, and
 * returning an expired token lets the BE 401 drive whatever downstream
 * error-handling the consumer already has. Withholding an expired but
 * still-attached-in-Redux token would introduce a regression on flag-OFF.
 */
export function getCachedAccessToken(): string | null {
  return cached?.token ?? null;
}

function currentAuthRevision(): number {
  return readAuthRevision();
}

// Compares a captured context against LIVE state — only meaningful when
// there's no shared-fetch result to classify against yet (the pre-launch
// bail-out before joining/launching anything). Once a `launchFetch` result
// exists, use `isStaleForCaller` instead: live state has, by definition,
// already moved past `expected` for a caller whose own accepted refresh is
// the very thing that just advanced it, so comparing against LIVE state at
// that point would misclassify every successful refresh as stale.
//
// Reads through `getCurrentAuthIdentitySnapshot` — the ONE shared
// definition of "current auth identity" also used by the operation-level
// commit gate, caller-level acceptance, and the axios destructive-exit
// fence (`isCapturedContextCurrent`). Both fields matter: revision alone
// misses an effective-provider change that never flows through the Redux
// reducers (the cookie/persisted-provider fallback `getEffectiveAuthProvider`
// reads on web/desktop respectively) — those never advance authRevision, so
// a revision-only compare would treat a genuine provider switch as still
// current.
function isExpectedStale(expected: { provider: string; authRevision: number }): boolean {
  return !sameIdentity(expected, getCurrentAuthIdentitySnapshot());
}

function sameIdentity(
  a: { provider: string; authRevision: number },
  b: { provider: string; authRevision: number }
): boolean {
  return a.provider === b.provider && a.authRevision === b.authRevision;
}

// Classifies a settled `launchFetch` result against ONE caller's own
// captured context — comparing against the identity the operation
// LAUNCHED under (`result.launchIdentity`), never against live state.
// This is what separates the shared provider operation (one fetch, one
// commit decision, made once) from each caller's own acceptance decision
// (made independently, once per caller): a leader whose own `expected`
// already predates the identity this fetch launched under gets `'stale'`
// here regardless of whether the fetch itself succeeded, while a
// differently-captured, still-current caller sharing the SAME fetch
// accepts the very same result as success — and a caller whose `expected`
// matches the launch identity is never told `'stale'` merely because that
// caller's OWN accepted refresh is the thing that advanced live state past
// it (see `toOutcome`'s unconditional post-commit read).
function isStaleForCaller(
  result: LaunchFetchResult,
  expected: { provider: string; authRevision: number } | undefined
): boolean {
  if (!expected) return false;
  return !sameIdentity(expected, result.launchIdentity);
}

function toOutcome(result: LaunchFetchResult): AccessTokenOutcome {
  if (result.stale) return { status: 'stale' };
  if (result.token === null) return { status: 'no-token' };
  // Post-commit read: unconditional, taken fresh here, never re-compared
  // against `expected`. Reusing the pre-commit comparison here would
  // discard every successful refresh's own revision-advancing write.
  return { status: 'success', token: result.token, authRevision: currentAuthRevision() };
}

/**
 * Same cache as {@link getAccessToken}, additionally reporting the
 * post-commit `authRevision` a successful token was published under, and
 * an `expected`-aware `'stale'` outcome for the 401-retry handler's
 * publication fence. `getAccessToken()` is a thin wrapper over this.
 */
export async function getAccessTokenWithRevision(
  options: GetAccessTokenOptions = {}
): Promise<AccessTokenOutcome> {
  // Suppressed-context gate. Returns before any fetch and before any atom
  // transition — deliberately NOT routed through the outcome pipeline. A
  // bare no-token classification would still write atoms and, on a
  // success outcome, `applySuccessOutcome` would still mirror the token
  // to Redux via `dispatch.auth.setToken` — either would defeat the
  // suppression. Applies to `forceRefresh: true` as well: a 401-retry
  // inside a suppressed context must not resurrect auth. Placed inside
  // `getAccessTokenWithRevision` (not in the scheduler's triggers) so one
  // gate covers every current and future consumer — the scheduler, the
  // axios read path, direct callers.
  if (getDefaultStore().get(authCacheSuppressedAtom)) {
    return { status: 'no-token' };
  }

  const { forceRefresh = false, expected } = options;
  const now = Date.now();

  // A forced refresh is in flight — ALL callers (forced or not, cache-hit
  // or miss) join it. The cached token is presumably the one that just
  // produced the 401 that triggered the forced refresh, so handing it back
  // to a concurrent non-forced caller would just produce a second 401.
  // This guard runs BEFORE the cache-hit branch for that reason.
  if (inFlight !== null && inFlightIsForcedRefresh) {
    const joinedResult = await inFlight;
    // Classify against the identity the shared fetch launched under — a
    // session replacement before or during the wait means this joiner's
    // captured context doesn't match, regardless of what the shared fetch
    // itself resolved to (see `isStaleForCaller`).
    if (isStaleForCaller(joinedResult, expected)) {
      return { status: 'stale' };
    }
    return toOutcome(joinedResult);
  }

  // Cache hit (non-forced).
  if (!forceRefresh && cached !== null && cached.expiresAt - now > 0) {
    const isNearExpiry = cached.expiresAt - now < REFRESH_SKEW_MS;
    if (isNearExpiry && inFlight === null) {
      // Stale-while-revalidate: return the cached value now, refresh in background.
      void launchFetch(false);
    }
    return {
      status: 'success',
      token: cached.token,
      authRevision: currentAuthRevision(),
    };
  }

  // Force-refresh: serialize behind a non-forced in-flight to prevent two
  // concurrent /api/auth0-session calls from racing Auth0's refresh-token
  // rotation detection (two concurrent refreshes both consume the shared
  // __session refresh token; whichever lands second is revoked, killing a
  // valid session). A forced fetch already in-flight is joined above.
  if (forceRefresh) {
    const genBeforeWait = generation;
    if (inFlight !== null && !inFlightIsForcedRefresh) {
      // Non-forced fetch is in flight — await it first (sequential, not concurrent).
      const existingResult = await inFlight;
      // If the in-flight returned a usable token that differs from the stale
      // one that triggered this retry, use it — no second round-trip needed.
      // (A logout during the await orphans the non-forced fetch to null via its
      // own generation guard, so existingResult.token is null and this branch is skipped.)
      if (existingResult.token !== null && existingResult.token !== options.priorToken) {
        // Stale check BEFORE acceptance — a session replacement before or
        // during the await means this joiner's captured context doesn't
        // match what THIS fetch launched under, even though the in-flight
        // fetch itself returned a usable token (for someone else's context).
        if (isStaleForCaller(existingResult, expected)) {
          return { status: 'stale' };
        }
        return {
          status: 'success',
          token: existingResult.token,
          authRevision: currentAuthRevision(),
        };
      }
      // Logout / cache-clear or suppression fired during the await.
      // Do NOT launch a forced fetch: it would run under the new post-logout
      // generation, pass the completion guard, and mirror-write a fresh token
      // to Redux — resurrecting the user after an intentional logout.
      if (
        generation !== genBeforeWait ||
        getDefaultStore().get(authCacheSuppressedAtom)
      ) {
        return { status: 'no-token' };
      }
      // The captured request context no longer matches live state — someone
      // else already changed the session since this caller's 401 was
      // captured. Abandon rather than publish a refresh for a context
      // that's already gone.
      if (expected && isExpectedStale(expected)) {
        return { status: 'stale' };
      }
      // Another forced sibling may have woken up from the same await first
      // and already called launchFetch(true). Join it instead of starting
      // a second concurrent forced read (which would race Auth0 rotation).
      // Order: this check MUST follow the generation/suppression/expected
      // guards above so a logout-or-stale-during-wait short-circuits before
      // we'd join a sibling.
      if (inFlight !== null && inFlightIsForcedRefresh) {
        const siblingResult = await inFlight;
        // Recheck against the sibling fetch's OWN launch identity — real
        // time passed again between the guards above and its result
        // landing.
        if (isStaleForCaller(siblingResult, expected)) {
          return { status: 'stale' };
        }
        return toOutcome(siblingResult);
      }
      // In-flight returned null (failed) or the same stale token → launch the
      // forced fetch now. The non-forced finally block already cleared inFlight,
      // so this becomes the sole in-flight call (safe from race).
    }
    // The operation's own commit gate is unconditional now (see
    // `identityChangedSinceLaunch` inside `launchFetch`) — it doesn't need
    // this caller's `expected` to protect the write. This caller still
    // separately classifies the settled result against its OWN `expected`
    // exactly like every joiner does — a leader whose `expected` was
    // ALREADY outdated before this fetch even launched gets 'stale' here
    // even though the fetch itself succeeded (for whatever identity it
    // actually launched under).
    const leaderResult = await launchFetch(true);
    if (isStaleForCaller(leaderResult, expected)) {
      return { status: 'stale' };
    }
    return toOutcome(leaderResult);
  }

  // Cache miss / expired: dedup on a non-forced in-flight. `expected` isn't
  // normally paired with a non-forced call, but if a caller does supply
  // one, fence this join too — "every" join, not just the forced ones.
  if (inFlight !== null) {
    const result = await inFlight;
    if (isStaleForCaller(result, expected)) {
      return { status: 'stale' };
    }
    return toOutcome(result);
  }

  const freshResult = await launchFetch(false);
  if (isStaleForCaller(freshResult, expected)) {
    return { status: 'stale' };
  }
  return toOutcome(freshResult);
}

export async function getAccessToken(
  options: GetAccessTokenOptions = {}
): Promise<string | null> {
  const outcome = await getAccessTokenWithRevision(options);
  return outcome.status === 'success' ? outcome.token : null;
}

function launchFetch(forceRefresh: boolean): Promise<LaunchFetchResult> {
  const myGeneration = generation;
  // The identity this operation launches under — exposed on every returned
  // result as `launchIdentity` (see below) so EVERY caller (not just the one
  // that happens to trigger this call) can classify the result against its
  // OWN `expected`: multiple callers with different captured contexts (a
  // stale leader, a current joiner, or vice versa) can all be waiting on
  // this SAME shared fetch. Read through the ONE shared auth-identity
  // snapshot (see `authIdentitySnapshot.ts`) — never Redux `authProvider`
  // directly — so this agrees with capture, caller-level acceptance, and
  // the destructive fence on what "current" means.
  const launchIdentity = getCurrentAuthIdentitySnapshot();
  // Provider-dependent budget: Auth0-desktop needs headroom for the
  // fallback's `getAuthSession()` join to resolve after the Rust
  // 10s connect timeout — see `AUTH0_DESKTOP_FETCH_TIMEOUT_MS`. All
  // other branches keep the standard 10s.
  const timeoutMs =
    safeGetEffectiveAuthProvider() === AuthProvider.AUTH0_DESKTOP
      ? AUTH0_DESKTOP_FETCH_TIMEOUT_MS
      : AUTH_FETCH_TIMEOUT_MS;
  // Must precede the IIFE assignment below so concurrent re-entries via
  // the early-return `if (inFlight !== null && inFlightIsForcedRefresh)` guard
  // see the forced state before the IIFE's first await yields. Reading
  // it after the IIFE would race: a caller arriving between IIFE
  // creation and this write would see the previous fetch's flag.
  inFlightIsForcedRefresh = forceRefresh;

  // Every branch below returns through this so `launchIdentity` never has
  // to be repeated (and can't drift) across the try/catch's several exits.
  const settle = (token: string | null, stale: boolean): LaunchFetchResult => ({
    token,
    stale,
    launchIdentity,
  });

  // Operation-level commit gate — ALWAYS runs, immediately before ANY
  // cache/atom/Redux write, regardless of whether any caller supplied an
  // `expected` context. Compares live identity against `launchIdentity`;
  // a live mismatch means the session moved on since this operation
  // started, so the write must not proceed. Guards even a proactive/SWR
  // fetch that no caller is pinned to — an Auth0 fetch that started
  // before a FHIR/EHR session begins must not cache- or Redux-mirror the
  // old Auth0 token under the new session just because nobody happened
  // to be awaiting it with a captured context.
  //
  // Runs BEFORE `applySuccessOutcome` advances the revision, so a
  // caller's own accepted refresh never sees its own write as a
  // mismatch — that half of the anti-livelock guarantee is unchanged;
  // the other half (never re-comparing `expected` against POST-commit
  // state) lives in `toOutcome`.
  //
  // Distinct from caller-level acceptance (`isStaleForCaller`, applied at
  // each call site in `getAccessTokenWithRevision` against THIS captured
  // `launchIdentity`): that decides whether ONE caller accepts an
  // already-settled result; this decides whether the operation commits
  // at all, for anyone.
  const identityChangedSinceLaunch = (): boolean =>
    !sameIdentity(getCurrentAuthIdentitySnapshot(), launchIdentity);

  // Per-fetch AbortController. On timeout, the underlying web fetch is
  // canceled — closes the connection slot and prevents the late response's
  // `Set-Cookie` from writing a rotated refresh token after the cache has
  // already given up. Desktop branches accept the signal but ignore it
  // because their underlying calls (Tauri IPC, Kinde SDK, oidc-client-ts)
  // don't support cancellation.
  const controller = new AbortController();

  // `promise` is captured by the IIFE's identity checks. The closure reads
  // the variable at await-time so the `inFlight === promise` identity check
  // correctly detects whether this fetch has been superseded. Initialized
  // with a placeholder so TypeScript's strict flow analysis sees a value;
  // the IIFE doesn't actually read `promise` until after its first `await`,
  // by which point the real assignment below has run.
  let promise: Promise<LaunchFetchResult> = Promise.resolve(settle(null, false));
  promise = (async () => {
    try {
      const result = await withTimeout(
        readFromProviderSourceOfTruth({
          forceRefresh,
          signal: controller.signal,
          generation: myGeneration,
        }),
        timeoutMs,
        () => controller.abort()
      );

      // Identity + generation + suppression guard: either another
      // launchFetch superseded us (forceRefresh arrived mid-flight), the
      // cache was cleared (clearAuthCache fired), or the context flipped
      // suppressed mid-fetch. In all three cases the result is stale —
      // returning it would clobber the newer state. Suppression must
      // discard here as well as at entry: a fetch launched BEFORE
      // suppression was set, resolving AFTER it, would otherwise pass
      // the identity + generation checks (the suppressing page's own
      // generation bump may live behind an `await import(...)`) and
      // both transition the atoms AND mirror-write the token back to
      // Redux — silently defeating the suppression contract.
      if (
        inFlight !== promise ||
        myGeneration !== generation ||
        getDefaultStore().get(authCacheSuppressedAtom)
      ) {
        // Generation changed (logout boundary) or suppression became active
        // mid-fetch — discard to null so stale results don't resurrect a
        // dead session or violate the suppression contract.
        //
        // The former "supersede" path (forced fetch replacing a running
        // non-forced fetch and reaching here with inFlight !== promise but
        // same generation) no longer exists: forceRefresh now serializes
        // behind any non-forced in-flight instead of launching concurrently.
        // clearAuthCache() is the only call that sets inFlight to null while
        // a fetch is running, and it always bumps the generation — so the
        // generation check subsumes the inFlight identity check.
        return settle(null, false);
      }
      // Operation-level commit gate — see `identityChangedSinceLaunch`
      // above. Checked here, before any atom/cache write, unconditionally.
      if (identityChangedSinceLaunch()) {
        return settle(null, true);
      }

      if (result === null) {
        applyBareNullOutcome({ forceRefresh });
        return settle(null, false);
      }

      // Empty-string token guard. An `accessToken: ''` is a contract
      // violation (same class as `isAuthenticated: true` + null
      // token); caching it would produce an authenticated-but-
      // unusable state where every API request goes out with an
      // empty bearer → 401 → forceRefresh returns the same '' →
      // livelock. Classify as transient — nothing cached, atoms
      // preserved, next fetch retries fresh. No in-repo producer
      // today; permanent hardening.
      if (result.accessToken === '') {
        applyFailureOutcome(new Error('AUTH_SESSION_TRANSIENT'), { forceRefresh });
        return settle(null, false);
      }

      return settle(applySuccessOutcome(result), false);
    } catch (error) {
      if (
        inFlight !== promise ||
        myGeneration !== generation ||
        getDefaultStore().get(authCacheSuppressedAtom)
      ) {
        // Same as the success-path guard: generation change or suppression —
        // discard to null. The supersede path no longer exists (see above).
        return settle(null, false);
      }
      if (identityChangedSinceLaunch()) {
        return settle(null, true);
      }
      applyFailureOutcome(error, { forceRefresh });
      return settle(null, false);
    } finally {
      // Only clear inFlight if it still points at us. Another launchFetch
      // (e.g. a forceRefresh that superseded a SWR background refresh) may
      // have replaced us — don't orphan its in-flight slot.
      if (inFlight === promise) {
        inFlight = null;
        inFlightIsForcedRefresh = false;
      }
    }
  })();

  inFlight = promise;
  return promise;
}

function applySuccessOutcome(session: AuthSessionMetadata): string {
  const store = getDefaultStore();
  const token = session.accessToken;
  const expiresAt = deriveExpiryFromToken(token);

  // Referential-stability short-circuit for the session-metadata atom.
  // The Auth0-desktop offline expiry loop (and any provider path that
  // rebuilds a `{ accessToken }` object per call) produces a fresh
  // reference for the same token every time — writing it here would
  // fire an atom notification on every read and, once axios reads
  // through the cache, cause a fan-out re-render per HTTP request.
  //
  // When the incoming token matches the previously-cached one, reuse
  // the prior session object for the atom write. Jotai's default
  // equality skips notification when the same reference is set
  // twice. Trade-off (deliberate): if the provider updates fields on
  // the session (region, userRefId, kindeUserId) but the token
  // itself is unchanged, subscribers won't see the new metadata
  // until the next rotation. Acceptable within a token lifetime —
  // the metadata refresh only matters after a genuine session
  // change, which always rotates the token.
  const sessionForAtom =
    cached !== null && cached.token === token ? cached.session : session;

  cached = { token, expiresAt, session: sessionForAtom };

  store.set(authStateAtom, 'authenticated');
  // Reset the reason atom — prevents a stale `'session_invalid'` from
  // leaking across a re-authentication boundary.
  store.set(authUnauthenticatedReasonAtom, null);
  store.set(authTransientFailureAtom, false);
  store.set(authSessionMetadataAtom, sessionForAtom);

  // Mirror the token to Redux for any caller still reading from
  // `store.select.auth.getToken` — the dual-run window is open while
  // some consumers haven't migrated to the cache yet. Removed when
  // `IS_DUAL_RUN_ACTIVE` is dropped. `dispatch.auth.setToken` is
  // same-value-guarded (advances `authRevision` only on a real change),
  // so a background SWR refresh of an unchanged token is a no-op here.
  if (IS_DUAL_RUN_ACTIVE) {
    reduxStore.dispatch.auth.setToken(token);
    // The request interceptor reads the external-launch token synchronously on
    // every retry. In the MD context the useMdAuthPersistence React effect that
    // bridges auth.token → the external-launch token fires asynchronously (next
    // React commit), so a 401-retry that triggers a force-refresh here would
    // still read the stale pre-refresh token. Mirror the fresh token
    // synchronously so the retried request carries the new Bearer, not the one
    // that just 401'd.
    if (MedicalDirectorSidebar.isMedicalDirector()) {
      setExternalLaunchToken({
        token,
        provider:
          getExternalLaunchProvider() ??
          reduxStore.getState().auth.authProvider ??
          AuthProvider.AUTH0,
      });
    }
  }

  return token;
}

interface OutcomeContext {
  forceRefresh: boolean;
}

function applyBareNullOutcome(ctx: OutcomeContext): void {
  const store = getDefaultStore();
  cached = null;
  store.set(authStateAtom, 'unauthenticated');
  // Bare null is NOT positive evidence of session invalidation. Reset
  // reason atom to null so the telemetry subscriber doesn't fire.
  store.set(authUnauthenticatedReasonAtom, null);
  store.set(authTransientFailureAtom, false);
  store.set(authSessionMetadataAtom, null);

  logger.info(
    {
      provider: safeGetEffectiveAuthProvider(),
      classification: 'bare_null',
      forceRefresh: ctx.forceRefresh,
    },
    '[auth-cache] bare-null outcome'
  );
}

function applyFailureOutcome(error: unknown, ctx: OutcomeContext): void {
  const store = getDefaultStore();
  const message = error instanceof Error ? error.message : String(error);
  // Definitive errors are thrown as `AUTH_SESSION_INVALID` (optionally
  // suffixed with `:reason=<value>` from the server). Match on prefix
  // so the plumbed reason is available for structured logging without
  // widening the outcome classifier.
  const isDefinitive = message.startsWith('AUTH_SESSION_INVALID');
  const serverReason = isDefinitive ? extractReason(message) : null;
  const provider = safeGetEffectiveAuthProvider();

  if (isDefinitive) {
    cached = null;
    // Set `authUnauthenticatedReasonAtom` BEFORE `authStateAtom` so
    // `useSessionInvalidationTelemetry` reads a consistent pair when
    // it observes the state transition. Its check gates on state first
    // (`state === 'unauthenticated'` opens the block), then reason
    // (`reason === 'session_invalid'` gates the telemetry fire). React
    // 18 batching normally collapses these two `store.set` calls into
    // one render, but if the subscriber ever runs mid-batch this
    // ordering guarantees reason is already `'session_invalid'` when
    // state flips — otherwise the subscriber's `prevState.current`
    // advances past the transition and the telemetry fire is
    // permanently missed for this session.
    store.set(authUnauthenticatedReasonAtom, 'session_invalid');
    store.set(authStateAtom, 'unauthenticated');
    store.set(authTransientFailureAtom, false);
    store.set(authSessionMetadataAtom, null);
    logger.warn(
      {
        provider,
        classification: 'definitive',
        forceRefresh: ctx.forceRefresh,
        error: message,
        reason: serverReason,
      },
      '[auth-cache] definitive session-invalid outcome'
    );
    return;
  }

  // Transient: preserve cached state and atoms; flag the failure so the
  // 401-retry handler can suppress a logout. By design, the cache does NOT
  // transition `authStateAtom` on transient — that rule exists so a valid
  // session isn't dropped over a network blip mid-flow. Boot-time
  // transients (first fetch fails before any state has been established)
  // need a retry trigger from the consumer side — the bootstrap scheduler
  // owns that retry loop. Cache stays strict on the four-outcome contract.
  store.set(authTransientFailureAtom, true);
  logger.info(
    {
      provider,
      classification: 'transient',
      forceRefresh: ctx.forceRefresh,
      error: message,
    },
    '[auth-cache] transient outcome'
  );
}

function extractReason(message: string): string | null {
  const marker = ':reason=';
  const idx = message.indexOf(marker);
  if (idx === -1) return null;
  const value = message.slice(idx + marker.length);
  return value.length > 0 ? value : null;
}

// `getEffectiveAuthProvider()` reads the cookie + Redux — safe under
// jest, but wrap in try/catch defensively so an unexpected read
// failure can't crash the outcome path (which is already handling an
// error).
function safeGetEffectiveAuthProvider(): string | null {
  try {
    return getEffectiveAuthProvider() ?? null;
  } catch {
    return null;
  }
}

function withTimeout<T>(
  promise: Promise<T>,
  ms: number,
  onTimeout?: () => void
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      onTimeout?.();
      reject(new Error('AUTH_FETCH_TIMEOUT'));
    }, ms);
  });
  return Promise.race([
    promise.finally(() => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    }),
    timeoutPromise,
  ]);
}

function deriveExpiryFromToken(token: string): number {
  // JWT exp is in seconds; cache uses epoch ms. Decode is best-effort —
  // any parse failure falls back to a conservative 5-minute TTL.
  try {
    const parts = token.split('.');
    const payloadPart = parts[1];
    if (parts.length === 3 && typeof payloadPart === 'string') {
      // base64url → base64 (browsers' atob accepts only the latter).
      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padding = (4 - (base64.length % 4)) % 4;
      const padded = base64 + '='.repeat(padding);
      const payload = JSON.parse(atob(padded)) as { exp?: unknown };
      if (typeof payload.exp === 'number') {
        return payload.exp * 1000;
      }
    }
  } catch {
    // Fall through to default TTL — token is opaque or malformed.
  }
  return Date.now() + FALLBACK_TTL_MS;
}

// ---------------------------------------------------------------------------
// Internal — test-only. Not part of the public API.
// ---------------------------------------------------------------------------

/**
 * @internal
 * Resets the module-level cache state. Tests must call this in `afterEach`
 * since the cache is a singleton.
 */
export function __resetCacheForTests(): void {
  cached = null;
  inFlight = null;
  inFlightIsForcedRefresh = false;
  generation = 0;

  const store = getDefaultStore();
  store.set(authStateAtom, 'loading');
  store.set(authUnauthenticatedReasonAtom, null);
  store.set(authSessionMetadataAtom, null);
  store.set(authTransientFailureAtom, false);
}

/** @internal — for tests that need to inspect the cached slot directly. */
export function __readCachedForTests(): CachedSlot | null {
  return cached;
}

/** @internal — for tests that need to inspect the generation counter. */
export function __readGenerationForTests(): number {
  return generation;
}
